import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  collection,
  query,
  where,
  onSnapshot,
  Unsubscribe,
  orderBy,
} from "firebase/firestore";
import { db, firestoreDb } from "../../../firebase/db";
import { BetEntryI, BetEntryIWithId, EventI } from "../../../types/types";
import { EVENT } from "../../../constants/routes";

const EventLeaderboard = () => {
  const params = useParams();
  const navigate = useNavigate();
  const eventId = params.eventId;
  const [lastUpdated, setLastUpdated] = useState<Date>();
  const [betData, setBetData] = useState<Array<BetEntryIWithId>>([]);
  const [event, setEvent] = useState<EventI>();

  useEffect(() => {
    let unsubscribe: Unsubscribe;
    const getEvent = async () => {
      if (eventId && !event) {
        const data = await db.events.get(eventId);
        if (data) {
          setEvent(data);
        }
      }
    };

    if (event) {
      if (!event?.paymentType) {
        return;
      }

      const q = query(
        collection(firestoreDb, "bets"),
        where("eventId", "==", event.id),
        orderBy("numOfCorrectAnswers", "desc"),
        orderBy("createdAt", "desc")
      );
      unsubscribe = onSnapshot(q, (querySnapshot) => {
        const bets: Array<BetEntryIWithId> = [];
        querySnapshot.forEach((doc) => {
          const id = doc.id;
          const data = doc.data() as BetEntryI;

          // TODO: based on the paymentType, we should show who has paid and who hasn't
          // For "venmo", we could have them verify they pay to view the leaderboard

          bets.push({ ...data, id });
        });

        setBetData(bets);
        setLastUpdated(new Date());
      });
    }

    getEvent();

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [eventId, event]);

  const handleUpdateBlacklist = async (
    bet: BetEntryIWithId,
    blackList: boolean
  ) => {
    if (eventId) {
      try {
        await db.bets.updateBlacklistById(eventId, bet.id, blackList);
      } catch (error) {
        console.error(error);
        alert("Unable to update blacklist. Please try again.");
      }
    }
  };

  if (event) {
    return (
      <div className="h-full p-5 w-full flex flex-col items-center">
        <div className="w-full max-w-xl">
          <h2 className="text-3xl mb-3 font-medium">{event.eventName}</h2>
          <h4 className="text-2xl mb-1">Leaderboard</h4>
          <p className="text-sm">
            Last updated: {lastUpdated?.toLocaleString()}
          </p>
          <p
            className="text-sm mb-5 link link-hover link-primary"
            onClick={() => {
              navigate(`/${EVENT}/${event.id}/leaderboard`);
            }}
          >
            View Public Version
          </p>
          <div className="flex flex-row mb-3">
            <h3 className="flex-1 text-lg">Name</h3>
            <h3 className="flex-1 text-lg"># Correct</h3>
            <h3 className="flex-1 text-lg">% Correct</h3>
            <div
              className="tooltip"
              data-tip="These people won't show up on the leaderboard"
            >
              <h3 className="flex-1 text-lg">Blacklist ❌</h3>
            </div>
          </div>
          <div className="flex flex-col gap-1">
            {betData.map((item, index) => {
              const percentCorrect = (
                (item.numOfCorrectAnswers / event.betItems.length) *
                100
              ).toFixed(1);
              return (
                <div
                  key={index}
                  className="flex flex-row border-b-[1px] border-gray-200"
                >
                  <div className="flex-1">
                    <p>
                      {item.firstName} {item.lastName}
                    </p>
                  </div>
                  <div className="flex-1 self-center justify-center text-center">
                    <p>{item.numOfCorrectAnswers}</p>
                  </div>
                  <div className="flex-1 self-center justify-center text-center">
                    <p>{percentCorrect}%</p>
                  </div>
                  <div className="flex-1 self-center justify-center text-center">
                    <input
                      type="checkbox"
                      className="toggle toggle-error"
                      checked={item.eventBlackList}
                      onChange={(e) =>
                        handleUpdateBlacklist(item, e.target.checked)
                      }
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full p-5 w-full flex flex-col items-center">
      <div className="w-full max-w-md">
        <p className="text-lg">Loading leaderboard...</p>
      </div>
    </div>
  );
};

export default EventLeaderboard;
